/**
 * Turn an UNKNOWN thrown/rejected value into something a human can read.
 *
 * A promise can reject with anything, and both client reporters used to assume
 * an `Error`: the non-Error branch fell through to `String(reason)`, which for a
 * plain object is literally "[object Object]" — and because `name`/`stack` were
 * read off the `instanceof Error` narrowing, they were dropped too. The whole
 * payload was discarded in the browser before the report was ever sent, so the
 * Diagnostics row carried no message, no name and no stack (b-01994).
 *
 * It compounds: app_errors fingerprints on source + message + stack + url, so a
 * constant message with no stack collapses EVERY non-Error rejection from one
 * page into a single group whose count climbs while every row stays unreadable.
 *
 * Pure string logic — no DOM, no node:crypto — so @pcp/shared stays lib:ES2022
 * and apps/api can unit-test it (mirrors error-fingerprint.ts).
 */
/**
 * Fields that commonly carry the human-readable half of a non-Error rejection —
 * a fetch-style envelope (`{ error: 'Forbidden' }`), an editor/upload handler
 * contract (`{ message, remove: true }`), a parser (`{ reason }`) or an RFC-7807
 * style body (`{ detail }`). Ordered by how specific each one usually is.
 */
const MESSAGE_KEYS = ['message', 'error', 'reason', 'detail', 'description', 'statusText'];
/** Short scalars worth appending to the message — they are what tells two apart. */
const CODE_KEYS = ['code', 'statusCode', 'status', 'errno'];
const MAX_MESSAGE = 4000;
/** A JSON fallback is a diagnostic hint, not a payload dump. */
const MAX_JSON = 800;
function isObject(v) {
    return typeof v === 'object' && v !== null;
}
/** A non-empty, genuinely printable scalar. */
function scalarText(v) {
    if (typeof v === 'string')
        return v.trim() || undefined;
    if (typeof v === 'number' || typeof v === 'bigint')
        return String(v);
    return undefined;
}
/**
 * `JSON.stringify` that cannot throw: cycles become "[Circular]", a BigInt and
 * anything else non-serializable degrade to a string rather than aborting the
 * whole description. Returns undefined when there is nothing useful to show.
 */
function safeJson(value) {
    try {
        const seen = new WeakSet();
        const json = JSON.stringify(value, (_key, val) => {
            if (typeof val === 'bigint')
                return String(val);
            if (typeof val === 'function')
                return `[Function ${val.name || 'anonymous'}]`;
            if (isObject(val)) {
                if (seen.has(val))
                    return '[Circular]';
                seen.add(val);
            }
            return val;
        });
        if (!json || json === '{}' || json === '[]' || json === 'null')
            return undefined;
        return json.length > MAX_JSON ? `${json.slice(0, MAX_JSON)}…` : json;
    }
    catch {
        return undefined;
    }
}
/**
 * The constructor name is the only type signal a non-Error value carries — it
 * separates a bare `{}` ("Object") from a library's own rejection class, and it
 * is what lands in the report's `name` column.
 */
function constructorName(value) {
    try {
        const name = value.constructor?.name;
        return typeof name === 'string' && name && name !== 'Object' ? name : undefined;
    }
    catch {
        return undefined; // a null-prototype object has no constructor
    }
}
/**
 * Describe any caught value. Never throws — a reporter that dies on the way to
 * reporting is worse than the error it was carrying.
 */
export function describeUnknownError(value) {
    try {
        const described = describeValue(value);
        return value instanceof Error ? withCauses(described, value) : described;
    }
    catch {
        return { message: 'Unknown error' };
    }
}
// ─── The `cause` chain (b-01997) ──────────────────────────────────────────────
// Node's fetch throws `TypeError: fetch failed` for EVERY network-level failure
// and puts the reason on `cause`: UND_ERR_CONNECT_TIMEOUT, ECONNRESET, ENOTFOUND,
// a TLS error. Recording only message + stack left a failed Zoho sync as a bare
// "fetch failed" that nobody could act on. Two places get the cause:
//   message  a short, STABLE hint — the cause's `code`, else its message — so a
//            connect timeout and a DNS failure fingerprint as different groups;
//   stack    a Java-style "Caused by:" section with the full text (target host,
//            timeout) APPENDED after the error's own frames. Node's own "fetch
//            failed" often has no frames, so there the cause's frame becomes the
//            top one. A cause-less error is returned untouched — its fingerprint
//            cannot move.
/** A real chain is 1–2 deep; a cycle (a.cause = b, b.cause = a) must still stop. */
const MAX_CAUSE_DEPTH = 3;
/** The message only needs a hint; the whole cause text is in the stack. */
const MAX_CAUSE_HINT = 200;
/** An AggregateError (one per attempted address) lists at most this many. */
const MAX_AGGREGATE_ERRORS = 3;
function causeChain(err) {
    const chain = [];
    const seen = new Set([err]);
    let next = err.cause;
    while (next !== undefined && next !== null && !seen.has(next) && chain.length < MAX_CAUSE_DEPTH) {
        chain.push(next);
        seen.add(next);
        next = isObject(next) ? next.cause : undefined;
    }
    return chain;
}
function clip(s, max) {
    return s.length > max ? `${s.slice(0, max)}…` : s;
}
function withCauses(described, err) {
    const chain = causeChain(err);
    if (!chain.length)
        return described;
    const hints = [];
    const sections = [];
    for (const link of chain) {
        const d = describeValue(link);
        const code = isObject(link) ? scalarText(link.code) : undefined;
        hints.push(code ?? clip(d.message, MAX_CAUSE_HINT));
        // A V8 stack already opens with "Name: message" (just "Name" when the message
        // is empty — Node's AggregateError); Firefox/Safari stacks and non-Error
        // causes do not, so the header is written once, here, in V8's own format.
        const header = link instanceof Error
            ? (link.message ? `${link.name}: ${link.message}` : link.name)
            : (d.name ? `${d.name}: ${d.message}` : d.message);
        const body = d.stack ? (d.stack.startsWith(header) ? d.stack.slice(header.length) : `\n${d.stack}`) : '';
        const inner = link instanceof Error ? link.errors : undefined;
        const errors = Array.isArray(inner)
            ? inner
                .slice(0, MAX_AGGREGATE_ERRORS)
                .map((e, i) => `\n  [${i}] ${describeValue(e).message}`)
                .join('')
            : '';
        sections.push(`Caused by: ${code ? `[${code}] ` : ''}${header}${errors}${body}`);
    }
    return {
        ...described,
        message: `${described.message} (cause: ${hints.join(' → ')})`.slice(0, MAX_MESSAGE),
        stack: [described.stack, ...sections].filter(Boolean).join('\n'),
    };
}
/** One value, WITHOUT its cause chain. */
function describeValue(value) {
    try {
        // An Error (including DOMException and every subclass) already answers all
        // three questions; keep that path byte-for-byte as it was.
        if (value instanceof Error) {
            return {
                message: (value.message || value.name || 'Unknown error').slice(0, MAX_MESSAGE),
                name: value.name,
                stack: value.stack,
            };
        }
        if (typeof value === 'string') {
            return { message: (value.trim() || 'Unknown error').slice(0, MAX_MESSAGE) };
        }
        if (value === null || value === undefined)
            return { message: 'Unknown error' };
        if (!isObject(value)) {
            // number | boolean | symbol | bigint — String() is genuinely readable here.
            return { message: String(value).slice(0, MAX_MESSAGE), name: typeof value };
        }
        const name = constructorName(value);
        // An Error-SHAPED object: something that crossed a structured-clone boundary
        // (a worker, postMessage, a serialized API envelope) arrives as a plain
        // object that still carries message/stack. Treat it as the error it was.
        let text;
        for (const key of MESSAGE_KEYS) {
            // A nested {error:{message}} envelope: unwrap one level, then give up —
            // deeper than that and the JSON fallback is the more honest answer.
            const raw = value[key];
            text = scalarText(raw) ?? (isObject(raw) ? scalarText(raw.message) : undefined);
            if (text)
                break;
        }
        const codes = [];
        for (const key of CODE_KEYS) {
            const code = scalarText(value[key]);
            // Only annotate; never let a status code alone stand in for a message.
            if (code)
                codes.push(`${key === 'statusCode' ? 'status' : key}: ${code}`);
        }
        // Nothing named a message — show the object itself rather than "[object Object]".
        if (!text)
            text = safeJson(value);
        const stack = scalarText(value.stack);
        const message = text
            ? codes.length ? `${text} (${codes.join(', ')})` : text
            : codes.length ? `${name ?? 'Non-Error'} (${codes.join(', ')})`
                : `${name ?? 'Non-Error'} rejection with no message`;
        return { message: message.slice(0, MAX_MESSAGE), name: name ?? 'Object', stack };
    }
    catch {
        return { message: 'Unknown error' };
    }
}
//# sourceMappingURL=error-describe.js.map